import type { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'

export type HighlightTier = 'checked' | 'needsReview'

/** All four compiled-directive tiers, including the two (`guaranteed`, `guidance`) that never
 * get per-item hover/click wiring in the editor drawer's decomposition rows — the display well
 * (see `findAllTierSpans` below) highlights all four, so it needs styles for all four. */
export type AnyHighlightTier = HighlightTier | 'guaranteed' | 'guidance'

export interface HighlightSpan {
    key: string
    tier: HighlightTier
    start: number
    end: number
}

export interface AnyHighlightSpan {
    key: string
    tier: AnyHighlightTier
    start: number
    end: number
}

/** aqe's `HighlightedTextArea` is tier-agnostic — it takes a plain `Record<tier, {background,
 * activeColor}>` rather than importing anything Brand-Voice-specific. This is that mapping for
 * Brand Voice's two linkable tiers, derived from the same `BRAND_VOICE_TIER_VISUAL` the rule
 * rows themselves render from, so the textarea and the rows can never visually disagree. */
export const HIGHLIGHT_TIER_STYLES: Record<HighlightTier, { background: string; activeColor: string }> = {
    checked: {
        background: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.checked.color}, transparent 75%)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.checked.color,
    },
    needsReview: {
        background: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.needsReview.color}, transparent 75%)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.needsReview.color,
    },
}

/** Same idea as `HIGHLIGHT_TIER_STYLES`, extended to all four tiers for the read-only display
 * well — see `AnyHighlightTier`. */
export const ALL_TIER_HIGHLIGHT_STYLES: Record<AnyHighlightTier, { background: string; activeColor: string }> = {
    guaranteed: {
        background: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.guaranteed.color}, transparent 75%)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guaranteed.color,
    },
    ...HIGHLIGHT_TIER_STYLES,
    guidance: {
        background: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.guidance.color}, transparent 75%)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guidance.color,
    },
}

/**
 * Builds the textarea<->rule highlight spans directly from a compiled directive's own
 * attribution (NS-2668) — each Checked/Needs-review item's `sources` is resolved server-side,
 * at compile time, against the exact body that produced it (see sealab-ts's
 * `resolveSources`/`source-attribution.ts`). This replaces the earlier client-side, post-hoc
 * string search entirely: that search could never resolve a genuine paraphrase (there was no
 * verbatim substring left to find), and the compiler is the one place that actually knows which
 * input text produced which rule.
 *
 * An item with an empty `sources` array is a normal, expected outcome — a paraphrase, or a
 * synthesized fact with nothing to quote — not a failure. It simply contributes no span, and
 * its row stays non-interactive (see `matchedSpanKeys` in brand-voice-decomposition.tsx).
 *
 * One item can resolve to more than one span (see `AttributedText`'s own docstring, e.g. a rule
 * merging two separate sentences) — each becomes its own `HighlightSpan` sharing that item's
 * key, so hovering/matching still works per-item rather than per-span.
 */
export function findVerbatimSpans(compiled: Pick<CompiledDirective, 'assertions' | 'unmapped'>): HighlightSpan[] {
    const spans: HighlightSpan[] = []

    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({ key: `checked-${i}`, tier: 'checked', start: source.start, end: source.end })
        })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({ key: `needsReview-${i}`, tier: 'needsReview', start: source.start, end: source.end })
        })
    })

    return spans.sort((a, b) => a.start - b.start)
}

/**
 * Same idea as `findVerbatimSpans`, extended to all four tiers — for the read-only display well
 * (`BrandVoiceWell`), which highlights everything a directive compiled into, not just the two
 * tiers the editor drawer links to rows. `resolved` (Guaranteed) and `style` (Guidance) carry
 * `sources` the same way `assertions`/`unmapped` do; nothing new server-side was needed.
 *
 * Kept separate from `findVerbatimSpans` rather than widening it in place — the editor drawer's
 * decomposition rows only ever wire hover/click for Checked/Needs-review, so handing it
 * Guaranteed/Guidance spans it has no matching interactive rows for would just add
 * non-interactive marks to that view with no corresponding change requested there.
 */
export function findAllTierSpans(
    compiled: Pick<CompiledDirective, 'resolved' | 'assertions' | 'style' | 'unmapped'>,
): AnyHighlightSpan[] {
    const spans: AnyHighlightSpan[] = []

    ;(compiled.resolved ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({ key: `guaranteed-${i}`, tier: 'guaranteed', start: source.start, end: source.end })
        })
    })
    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({ key: `checked-${i}`, tier: 'checked', start: source.start, end: source.end })
        })
    })
    ;(compiled.style?.sources ?? []).forEach((source) => {
        spans.push({ key: 'guidance-0', tier: 'guidance', start: source.start, end: source.end })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({ key: `needsReview-${i}`, tier: 'needsReview', start: source.start, end: source.end })
        })
    })

    return spans.sort((a, b) => a.start - b.start)
}
